import { Controller } from "@hotwired/stimulus";
import Sortable from "sortablejs";

export default class extends Controller {
  static values = { url: String, franceFilter: String };
  static targets = [
    "item",
    "franceIncludeToggle",
    "franceIncludeIndicator",
    "franceExcludeToggle",
    "franceExcludeIndicator",
  ];

  connect() {
    console.log("🔌 Contrôleur hierarchy-sortable connecté");
    console.log("🔗 URL configurée:", this.urlValue);
    this.initializeSortable();
    this.initializeCollapsedState();
    if (!this.hasFranceFilterValue) {
      this.franceFilterValue = "all";
    }
    this.updateFranceFilterButtons();
    this.applyFranceFilter();
  }

  disconnect() {
    if (this.sortable) {
      this.sortable.destroy();
    }
  }

  initializeSortable() {
    // Trouver le conteneur de hiérarchie à l'intérieur de l'élément contrôleur
    const hierarchyContainer = this.element.querySelector(
      "#hierarchy-sortable",
    );

    this.sortable = Sortable.create(hierarchyContainer, {
      group: {
        name: "hierarchy",
        pull: true,
        put: true,
      },
      animation: 200,
      fallbackOnBody: true,
      swapThreshold: 0.65,
      handle: ".cursor-move",
      draggable: ".hierarchy-item",
      ghostClass: "sortable-ghost",
      chosenClass: "sortable-chosen",
      dragClass: "sortable-drag",
      onStart: (evt) => {
        document.body.classList.add("dragging");
      },
      onEnd: (evt) => {
        document.body.classList.remove("dragging");
        this.handleDrop(evt);
      },
    });

    // Initialiser les zones de drop imbriquées pour les topics
    this.initializeNestedDropZones();
  }

  initializeNestedDropZones() {
    const hierarchyContainer = this.element.querySelector(
      "#hierarchy-sortable",
    );
    const topicContainers = hierarchyContainer.querySelectorAll(
      '.hierarchy-item[data-type="topic"] .ml-8',
    );

    topicContainers.forEach((container) => {
      // Détruire l'instance existante si elle existe
      if (container.sortableInstance) {
        container.sortableInstance.destroy();
        container.sortableInstance = null;
      }

      // S'assurer que la zone a une hauteur minimale même vide
      if (!container.style.minHeight) {
        container.style.minHeight = "40px";
      }

      const sortable = Sortable.create(container, {
        group: {
          name: "hierarchy",
          pull: true,
          put: true,
        },
        animation: 200,
        fallbackOnBody: true,
        swapThreshold: 0.65,
        handle: ".cursor-move",
        draggable: ".hierarchy-item",
        ghostClass: "sortable-ghost",
        chosenClass: "sortable-chosen",
        dragClass: "sortable-drag",
        onStart: (evt) => {
          document.body.classList.add("dragging");
        },
        onEnd: (evt) => {
          document.body.classList.remove("dragging");
          this.handleDrop(evt);
        },
      });

      container.sortableInstance = sortable;
    });
  }

  initializeCollapsedState() {
    // Tous les topics commencent collapsés par défaut
    const topics = this.element.querySelectorAll(
      '.hierarchy-item[data-type="topic"]',
    );
    topics.forEach((topic) => {
      const content = topic.querySelector(".topic-content");
      const toggleButton = topic.querySelector(".collapse-toggle");
      const icon = toggleButton?.querySelector(".collapse-icon");

      if (content && toggleButton) {
        // S'assurer que le contenu est caché et l'icône pointée vers la droite (chevron-down avec rotation -90deg)
        content.classList.add("hidden");
        if (icon) {
          icon.classList.remove("rotate-0");
          icon.classList.add("-rotate-90");
        }
      }
    });
  }

  toggleFranceOnly() {
    this.franceFilterValue = this.franceFilterValue === "only" ? "all" : "only";
    this.updateFranceFilterButtons();
    this.applyFranceFilter();
  }

  toggleFranceExclude() {
    this.franceFilterValue =
      this.franceFilterValue === "exclude" ? "all" : "exclude";
    this.updateFranceFilterButtons();
    this.applyFranceFilter();
  }

  updateFranceFilterButtons() {
    this.updateToggleState(
      this.hasFranceIncludeToggleTarget ? this.franceIncludeToggleTarget : null,
      this.hasFranceIncludeIndicatorTarget
        ? this.franceIncludeIndicatorTarget
        : null,
      this.franceFilterValue === "only",
    );

    this.updateToggleState(
      this.hasFranceExcludeToggleTarget ? this.franceExcludeToggleTarget : null,
      this.hasFranceExcludeIndicatorTarget
        ? this.franceExcludeIndicatorTarget
        : null,
      this.franceFilterValue === "exclude",
    );
  }

  updateToggleState(button, indicator, active) {
    if (!button) return;

    const activeClasses = [
      "bg-emerald-500",
      "border-emerald-600",
      "text-white",
      "hover:bg-emerald-600",
      "hover:border-emerald-700",
    ];
    const inactiveClasses = [
      "bg-white",
      "border-gray-200",
      "text-gray-700",
      "hover:bg-blue-50",
      "hover:border-blue-200",
    ];

    button.classList.remove(...(active ? inactiveClasses : activeClasses));
    button.classList.add(...(active ? activeClasses : inactiveClasses));
    button.setAttribute("aria-pressed", active ? "true" : "false");

    if (indicator) {
      indicator.classList.toggle("bg-emerald-300", active);
      indicator.classList.toggle("bg-gray-300", !active);
    }
  }

  applyFranceFilter() {
    const filterMode = this.franceFilterValue || "all";
    const items = this.element.querySelectorAll(
      '.hierarchy-item[data-type="item"]',
    );

    items.forEach((item) => {
      const codes = (item.dataset.platformCodes || "")
        .split(",")
        .map((code) => code.trim())
        .filter((code) => code.length > 0);
      const hasFrance = codes.includes("mii_fr");

      let shouldHide = false;
      if (filterMode === "only") {
        shouldHide = !hasFrance;
      } else if (filterMode === "exclude") {
        shouldHide = hasFrance;
      }

      item.classList.toggle("hidden", shouldHide);
    });

    const topics = Array.from(
      this.element.querySelectorAll('.hierarchy-item[data-type="topic"]'),
    );

    if (filterMode === "all") {
      topics.forEach((topic) => topic.classList.remove("hidden"));
      return;
    }

    topics.reverse().forEach((topic) => {
      const shouldHide = !this.topicHasVisibleDescendant(topic);
      topic.classList.toggle("hidden", shouldHide);
    });
  }

  topicHasVisibleDescendant(topicElement) {
    const childContainer = topicElement.querySelector(".topic-content");
    if (!childContainer) return false;

    return Array.from(childContainer.querySelectorAll(".hierarchy-item")).some(
      (child) => !child.classList.contains("hidden"),
    );
  }

  toggleCollapse(event) {
    event.stopPropagation();
    const button = event.currentTarget;
    const topicId = button.dataset.topicId;
    const topicElement = this.element.querySelector(
      `[data-id="${topicId}"][data-type="topic"]`,
    );

    if (!topicElement) return;

    const content = topicElement.querySelector(".topic-content");
    const icon = button.querySelector(".collapse-icon");

    if (!content || !icon) return;

    // Toggle la visibilité
    const isHidden = content.classList.contains("hidden");

    if (isHidden) {
      // Expand
      content.classList.remove("hidden");
      icon.classList.remove("-rotate-90");
      icon.classList.add("rotate-0");
    } else {
      // Collapse
      content.classList.add("hidden");
      icon.classList.remove("rotate-0");
      icon.classList.add("-rotate-90");
    }
  }

  saveCollapsedStates() {
    const states = {};
    const topics = this.element.querySelectorAll(
      '.hierarchy-item[data-type="topic"]',
    );

    topics.forEach((topic) => {
      const topicId = topic.dataset.id;
      const content = topic.querySelector(".topic-content");
      if (content) {
        states[topicId] = content.classList.contains("hidden");
      }
    });

    return states;
  }

  restoreCollapsedStates(states) {
    Object.keys(states).forEach((topicId) => {
      const topicElement = this.element.querySelector(
        `[data-id="${topicId}"][data-type="topic"]`,
      );

      if (!topicElement) return;

      const content = topicElement.querySelector(".topic-content");
      const toggleButton = topicElement.querySelector(".collapse-toggle");
      const icon = toggleButton?.querySelector(".collapse-icon");

      if (content && toggleButton && icon) {
        const shouldBeCollapsed = states[topicId];

        if (shouldBeCollapsed) {
          content.classList.add("hidden");
          icon.classList.remove("rotate-0");
          icon.classList.add("-rotate-90");
        } else {
          content.classList.remove("hidden");
          icon.classList.remove("-rotate-90");
          icon.classList.add("rotate-0");
        }
      }
    });
  }

  canMove(evt) {
    return true;
  }

  isDescendant(childId, parentId) {
    const childElement = this.element.querySelector(`[data-id="${childId}"]`);
    if (!childElement) return false;

    let current = childElement.parentElement;
    while (current && current !== this.element) {
      if (current.classList && current.classList.contains("hierarchy-item")) {
        if (current.dataset.id === parentId) {
          return true;
        }
      }
      current = current.parentElement;
    }
    return false;
  }

  findParentTopicElement(container) {
    const hierarchyContainer = this.element.querySelector(
      "#hierarchy-sortable",
    );
    let current = container.parentElement;
    while (current && current !== hierarchyContainer) {
      if (
        current.classList &&
        current.classList.contains("hierarchy-item") &&
        current.dataset.type === "topic"
      ) {
        return current;
      }
      current = current.parentElement;
    }
    return null;
  }

  findParentTopicId(container) {
    const element = this.findParentTopicElement(container);
    return element ? element.dataset.id : null;
  }

  handleDrop(evt) {
    // Sauvegarder l'état collapsed avant la réinitialisation
    const collapsedStates = this.saveCollapsedStates();

    // Mettre à jour les états vides et les compteurs après le drop
    this.updateEmptyDropZoneMessages();
    this.updateTopicCounters();

    // Réinitialiser les zones de drop après un changement
    setTimeout(() => {
      this.initializeNestedDropZones();
      // Restaurer l'état collapsed après la réinitialisation
      this.restoreCollapsedStates(collapsedStates);
      this.applyFranceFilter();
    }, 200);
  }

  saveHierarchy() {
    console.log("🎯 BOUTON CLIQUÉ - Début de la sauvegarde");
    const hierarchy = this.buildHierarchy();
    console.log("📋 Hiérarchie construite:", hierarchy);
    console.log("🔗 URL de sauvegarde:", this.urlValue);

    // Afficher l'indicateur de chargement
    this.showLoading();

    // Utiliser fetch pour plus de simplicité
    fetch(this.urlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document
          .querySelector('meta[name="csrf-token"]')
          .getAttribute("content"),
        "X-Requested-With": "XMLHttpRequest",
        Accept: "application/json",
      },
      body: JSON.stringify({ hierarchy: hierarchy }),
    })
      .then((response) => response.json())
      .then((data) => {
        console.log("📡 Réponse reçue:", data);
        this.hideLoading();

        if (data.status === "success") {
          console.log("✅ Sauvegarde réussie");
          this.displayFlashMessage(data.message, "success");
          // Masquer les messages de drop zone vides après la sauvegarde
          this.updateEmptyDropZoneMessages();
        } else {
          console.error("❌ Erreur:", data.message);
          this.displayFlashMessage(data.message, "error");
        }
      })
      .catch((error) => {
        console.error("❌ Erreur réseau:", error);
        this.hideLoading();
        this.displayFlashMessage(
          "Erreur réseau lors de la sauvegarde",
          "error",
        );
      });
  }

  buildHierarchy() {
    const hierarchy = [];
    const hierarchyContainer = this.element.querySelector(
      "#hierarchy-sortable",
    );
    this.processContainer(hierarchyContainer, null, hierarchy);
    return hierarchy;
  }

  processContainer(container, parentId, hierarchy) {
    const directChildren = Array.from(container.children).filter((child) =>
      child.classList.contains("hierarchy-item"),
    );

    directChildren.forEach((item, index) => {
      const itemData = {
        id: item.dataset.id,
        type: item.dataset.type,
        parent_id: parentId,
        position: index + 1,
      };
      hierarchy.push(itemData);

      // Si c'est un topic, traiter ses enfants
      if (item.dataset.type === "topic") {
        const childContainer = item.querySelector(".ml-8");
        if (childContainer) {
          this.processContainer(childContainer, item.dataset.id, hierarchy);
        }
      }
    });
  }

  showLoading() {
    // Afficher un indicateur global
    this.loadingIndicator = document.createElement("div");
    this.loadingIndicator.className =
      "fixed top-4 right-4 bg-blue-500 text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center gap-2";
    this.loadingIndicator.innerHTML = `
      <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
      <span>Sauvegarde...</span>
    `;
    document.body.appendChild(this.loadingIndicator);
  }

  hideLoading() {
    if (this.loadingIndicator && this.loadingIndicator.parentNode) {
      this.loadingIndicator.parentNode.removeChild(this.loadingIndicator);
      this.loadingIndicator = null;
    }
  }

  displayFlashMessage(message, type) {
    // Créer le HTML du flash message en utilisant le même format que le layout
    const flashContainer = document.createElement("div");
    flashContainer.className =
      "fixed inset-x-0 top-0 flex items-end justify-right px-4 py-6 sm:p-6 justify-end z-50 pointer-events-none";

    const alertClass =
      type === "success"
        ? "bg-emerald-500 border-emerald-700"
        : "bg-red-500 border-red-700";

    flashContainer.innerHTML = `
      <div data-controller="alert" class="max-w-sm w-full shadow-lg rounded-sm px-4 py-3 rounded relative border-l-4 text-white pointer-events-auto ${alertClass}">
        <div class="p-2">
          <div class="flex items-start">
            <div class="ml-3 w-0 flex-1 pt-0.5">
              <p class="text-sm leading-5 font-medium">
                ${message}
              </p>
            </div>
            <div class="ml-4 flex-shrink-0 flex">
              <button data-action="alert#close" class="inline-flex text-white focus:outline-none focus:text-gray-300 transition ease-in-out duration-150">
                <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(flashContainer);

    // Auto-hide after 5 seconds
    setTimeout(() => {
      if (flashContainer.parentNode) {
        flashContainer.parentNode.removeChild(flashContainer);
      }
    }, 5000);
  }

  updateEmptyDropZoneMessages() {
    // Parcourir tous les topics et masquer/afficher les messages selon leur contenu
    const hierarchyContainer = this.element.querySelector(
      "#hierarchy-sortable",
    );
    const topics = hierarchyContainer.querySelectorAll(
      '.hierarchy-item[data-type="topic"]',
    );

    topics.forEach((topic) => {
      const childContainer = topic.querySelector(".ml-8");
      const hasChildren =
        childContainer?.querySelectorAll(".hierarchy-item").length > 0;
      const isSubTopic =
        topic.dataset.parentId && topic.dataset.parentId !== "";

      // Chercher ou créer le message d'état vide
      let emptyMessage = childContainer?.querySelector(".text-gray-400.italic");

      if (hasChildren) {
        // Masquer ou supprimer le message s'il y a des enfants
        if (emptyMessage) {
          emptyMessage.style.display = "none";
        }
      } else {
        // Créer le message s'il n'existe pas
        if (!emptyMessage) {
          emptyMessage = document.createElement("div");
          emptyMessage.className =
            "text-sm text-gray-400 italic py-2 px-3 border-2 border-dashed border-gray-200 rounded-lg bg-gray-50";
          childContainer?.appendChild(emptyMessage);
        }

        // Afficher le message approprié selon le niveau
        emptyMessage.style.display = "block";
        if (isSubTopic) {
          emptyMessage.textContent =
            "Déposez des articles ici (pas de sous-topics)";
        } else {
          emptyMessage.textContent = "Déposez des topics ou articles ici";
        }
      }
    });
  }

  updateTopicCounters() {
    // Mettre à jour les compteurs d'éléments pour chaque topic
    const hierarchyContainer = this.element.querySelector(
      "#hierarchy-sortable",
    );
    const topics = hierarchyContainer.querySelectorAll(
      '.hierarchy-item[data-type="topic"]',
    );

    topics.forEach((topic) => {
      const childContainer = topic.querySelector(".ml-8");
      const childTopics =
        childContainer?.querySelectorAll('.hierarchy-item[data-type="topic"]')
          .length || 0;
      const childItems =
        childContainer?.querySelectorAll('.hierarchy-item[data-type="item"]')
          .length || 0;

      // Chercher l'élément compteur existant (celui qui contient "sous-topics" ou "articles")
      let counterElement = null;
      const potentialCounters = topic.querySelectorAll(
        ".text-xs.text-gray-500",
      );
      for (let element of potentialCounters) {
        if (
          element.textContent.includes("sous-topics") ||
          element.textContent.includes("articles")
        ) {
          counterElement = element;
          break;
        }
      }

      if (childTopics > 0 || childItems > 0) {
        if (!counterElement) {
          // Créer l'élément compteur s'il n'existe pas
          counterElement = document.createElement("span");
          counterElement.className = "text-xs text-gray-500 ml-2";

          // L'insérer après le texte "(Topic)"
          const topicLabel = topic.querySelector(".text-sm.text-blue-600");
          if (topicLabel) {
            topicLabel.parentNode.insertBefore(
              counterElement,
              topicLabel.nextSibling,
            );
          }
        }

        // Mettre à jour le texte du compteur
        counterElement.textContent = `(${childTopics} sous-topics, ${childItems} articles)`;
      } else {
        // Supprimer le compteur s'il n'y a plus d'enfants
        if (counterElement) {
          counterElement.remove();
        }
      }
    });
  }
}
